import {
  Children,
  cloneElement,
  isValidElement,
  useEffect,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from 'react';
import { Status, Wrapper } from '@googlemaps/react-wrapper';
import { env } from 'env.mjs';
import { useFormContext } from 'react-hook-form';
import { MdLocationPin } from 'react-icons/md';

import { ButtonWithLogo } from '@/components/common/ButtonWithLogo/ButtonWithLogo';
import { Button } from '@/components/ui/Button';

import InputFormContext from '../Inputs/InputFormContext';
import { useDeepCompareEffectForMaps } from './hooks/useDeepCompareEffect';

/*-------------- config --------------*/
export interface IGoogleMapViewerProps {
  onClickFunc?: (data: google.maps.GeocoderResult) => void;
  autoLocalization?: boolean;
  className?: string;
  handleOpen: any;
  currentId: any;
}

interface MapProps extends google.maps.MapOptions {
  style: { [key: string]: string };
  onClick?: (e: google.maps.MapMouseEvent) => void;
  onIdle?: (map: google.maps.Map) => void;
  children?: React.ReactNode;
}

export const loadingRender = (status: Status) => {
  if (status == 'FAILURE') {
    alert('No se pudo geocodificar la ubicación.');
    return <></>;
  }

  return <div className="loader h-full w-full" />;
};

/*-------------- component --------------*/

/**
 * googleMap Component
 * set options (listeners and render the map)
 * @param MapProps
 * @returns
 */
export const MapComponent: FC<MapProps> = ({ onClick, onIdle, children, style, ...options }) => {
  // hooks
  const ref = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map>();

  useEffect(() => {
    if (ref.current && !map) {
      setMap(new window.google.maps.Map(ref.current, {}));
    }
  }, [ref, map]);

  useDeepCompareEffectForMaps(() => {
    if (map) {
      map.setOptions(options);
    }
  }, [map, options]);

  useEffect(() => {
    if (map) {
      ['click', 'idle'].forEach((eventName) => google.maps.event.clearListeners(map, eventName));

      if (onClick) {
        map.addListener('click', onClick);
      }

      if (onIdle) {
        map.addListener('idle', () => onIdle(map));
      }
    }
  }, [map, onClick, onIdle]);

  return (
    <>
      <div ref={ref} style={style} />
      {Children.map<ReactNode, ReactNode>(children, (child) => {
        if (isValidElement(child)) {
          // set the map prop on the child component
          return cloneElement(child as React.ReactElement, { map });
        }
      })}
    </>
  );
};

export const Marker = (options: google.maps.MarkerOptions) => {
  const [marker, setMarker] = useState<google.maps.Marker>();

  useEffect(() => {
    if (!marker) {
      setMarker(new google.maps.Marker());
    }

    // remove marker from map on unmount
    return () => {
      if (marker) {
        marker.setMap(null);
      }
    };
  }, [marker]);

  useEffect(() => {
    if (marker) {
      marker.setOptions(options);
    }
  }, [marker, options]);

  return null;
};

const GoogleMapViewer: FC<IGoogleMapViewerProps> = ({
  autoLocalization,
  className,
  onClickFunc,
  handleOpen,
  currentId,
}) => {
  const {
    register,

    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext();

  // hooks
  const [clicks, setClicks] = useState<google.maps.LatLng>();
  const [zoom, setZoom] = useState(6); // initial zoom
  const [center, setCenter] = useState<google.maps.LatLngLiteral>({
    lat: 4.573156896660341,
    lng: -74.0928881330088,
  });

  // functions
  const geocodeLocation = (lat: number, lng: number) => {
    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode(
      { location: { lat, lng } },
      (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
        if (status === 'OK' && results && results[0]) {
          const placeDetails = results[0];
          if (onClickFunc) {
            onClickFunc(placeDetails);
          }
        }
      }
    );
  };

  const onClick = (e: google.maps.MapMouseEvent) => {
    setClicks(e.latLng!);
    geocodeLocation(e.latLng?.lat() ?? 0, e.latLng?.lng() ?? 0);
  };

  const onIdle = (m: google.maps.Map) => {
    setZoom(m.getZoom()!);
    setCenter(m.getCenter()!.toJSON());
  };

  const getCurrentPosition = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        const { latitude, longitude } = position.coords;
        geocodeLocation(latitude, longitude);

        setCenter({
          lat: latitude,
          lng: longitude,
        });

        const location = new google.maps.LatLng(latitude, longitude);

        setZoom(18);

        setClicks(location);
      });
    }
  };
  return (
    <div className={className}>
      <Wrapper apiKey={env.NEXT_PUBLIC_GOOGLE_MAP_KEY} render={loadingRender}>
        <MapComponent
          center={center}
          onClick={onClick}
          onIdle={onIdle}
          zoom={zoom}
          style={{
            flexGrow: '1' /* esto si el elemento padre es flex y se desea que abarque todo */,
          }}
        >
          <Marker position={clicks} />
        </MapComponent>
      </Wrapper>
      <div className=" mx-auto mt-4 flex w-9/12 gap-6">
        {' '}
        <InputFormContext
          // disabled
          currentId={currentId}
          // currentValue={getValues(currentId)}
          currentValue={watch(currentId)}
          className="disabled w-full"
          title="Dirección"
        />
      </div>

      {autoLocalization ? (
        <div className="mt-5 flex justify-center gap-7">
          <Button onClick={getCurrentPosition} className="">
            <MdLocationPin className="h-[20px] w-[20px]" /> Mi ubicación
          </Button>
          <ButtonWithLogo
            onClick={handleOpen}
            nameButton={'Cancelar'}
            className="border-none bg-red-500  font-bold text-white"
          />
          <ButtonWithLogo
            onClick={handleOpen}
            nameButton={'Confirmar'}
            className="border-none bg-green font-bold text-white"
          />
        </div>
      ) : null}
    </div>
  );
};

export default GoogleMapViewer;
